// @file Screen-recorder-specific state for the combined AV recorder.
// Extracted from useContentPickerAvRecorderStore to keep concerns separated.
// Dependencies: useGlobalContentPickerStore, useGlobalSnackbarStore, useAudioDevicesStore (no dependency on the AV store).

import { trackEvent } from '@@/bits/analytics'
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { ContentPickerSource } from '@@/bits/content_picker'
import type { ScreenRecorderPickerConfig, ScreenRecorderPickerState } from '@@/bits/content_picker_configs'
import { ScreenRecorderStatus } from '@@/bits/content_picker_configs'
import device from '@@/bits/device'
import { captureException, captureMessage } from '@@/bits/error_tracker'
import { p__, __ } from '@@/bits/intl'
import { getMixedSystemAndMicAudio } from '@@/bits/media_streams'
import {
  SCREEN_RECORDER_TIMESLICE,
  supportedAudioCodec,
  supportedScreenRecordingCodec,
  supportedScreenRecordingExtension,
} from '@@/bits/media_support'
import { DEFAULT_VIDEO_LENGTH_LIMIT_IN_SECONDS } from '@@/bits/plans_helper'
import { VideoLengthLimits } from '@@/bits/post_properties'
import { secondsToMmSs } from '@@/bits/time'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useAudioDevicesStore } from '@@/pinia/useAudioDevicesStore'
import { useGlobalContentPickerStore } from '@@/pinia/useGlobalContentPickerStore'
import { useWakeLock } from '@vueuse/core'
import ysFixWebmDuration from 'fix-webm-duration'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'

interface ActiveScreenRecording {
  mediaRecorder: MediaRecorder
  stream: MediaStream
  streamChunks: Blob[]
  microphoneTrack: MediaStreamTrack | null
  videoAudioTrack: MediaStreamTrack | null
  mediaRecorderTimerId: ReturnType<typeof setInterval> | null
  recordingDurationMs: number
  recordingStartTimeMs: number
}

interface RecordingBarController {
  elapsedRecordingTimeDisplay: { minutes: string; seconds: string }
  timeLimitDisplay: { minutes: string; seconds: string }
  isReachingRecordingLimit: boolean
  isMicOn: boolean
  stopRecording: () => void
  toggleMic: () => void
}

const useContentPickerAvScreenRecorderStore = defineStore('contentPickerAvScreenRecorderStore', () => {
  const globalStore = useGlobalContentPickerStore()

  // #region Config

  const screenRecorderConfig = computed<ScreenRecorderPickerConfig | undefined>(() => {
    return globalStore.globalScreenRecordingSession?.pickerConfigs[ContentPickerSource.ScreenshareRecorder]
  })

  const screenRecordingLengthLimit = computed(
    () => screenRecorderConfig.value?.screenRecordingLengthLimit ?? DEFAULT_VIDEO_LENGTH_LIMIT_IN_SECONDS,
  )

  const screenRecordingSessionId = computed<string | null>(() => globalStore.globalScreenRecordingSession?.id ?? null)

  const currentScreenRecordingLengthLimitSetting = computed(() => {
    return VideoLengthLimits.find((limit) => limit.value === screenRecordingLengthLimit.value)
  })

  // #endregion Config

  // #region Picker state

  const DEFAULT_SCREEN_RECORDER_PICKER_STATE: ScreenRecorderPickerState = {
    source: ContentPickerSource.ScreenshareRecorder,
    screenRecorderStatus: ScreenRecorderStatus.Connecting,
    returnToUrl: null,
  }

  function readPickerState(): ScreenRecorderPickerState {
    const sid = screenRecordingSessionId.value
    if (sid == null) return DEFAULT_SCREEN_RECORDER_PICKER_STATE
    const state = globalStore.sessions[sid]?.pickerStates[ContentPickerSource.ScreenshareRecorder]
    if (state?.source === ContentPickerSource.ScreenshareRecorder) {
      return state as ScreenRecorderPickerState
    }
    return DEFAULT_SCREEN_RECORDER_PICKER_STATE
  }

  function patchPickerState(patch: Partial<Omit<ScreenRecorderPickerState, 'source'>>): void {
    const sid = screenRecordingSessionId.value
    if (sid == null) return
    globalStore.setPickerStateForSession(sid, ContentPickerSource.ScreenshareRecorder, {
      ...readPickerState(),
      ...patch,
    })
  }

  // #endregion Picker state

  // #region Status

  const screenRecorderStatus = computed<ScreenRecorderStatus>(() => readPickerState().screenRecorderStatus)

  const setScreenRecorderStatus = (value: ScreenRecorderStatus): void =>
    patchPickerState({ screenRecorderStatus: value })

  const isScreenRecordingInProgress = computed<boolean>(() => {
    return screenRecordingSessionId.value != null && screenRecorderStatus.value === ScreenRecorderStatus.Recording
  })

  const hasUnsavedScreenWork = computed<boolean>(() => {
    const status = screenRecorderStatus.value
    return (
      status === ScreenRecorderStatus.Recording ||
      status === ScreenRecorderStatus.Paused ||
      status === ScreenRecorderStatus.Previewing
    )
  })

  const hasStartedRecordingScreen = computed<boolean>(() => {
    const status = screenRecorderStatus.value
    return (
      status === ScreenRecorderStatus.Recording ||
      status === ScreenRecorderStatus.Paused ||
      status === ScreenRecorderStatus.Previewing ||
      status === ScreenRecorderStatus.Saving ||
      status === ScreenRecorderStatus.Submitted
    )
  })

  // #endregion Status

  // #region Navigation

  const returnToUrl = computed<string | null>(() => readPickerState().returnToUrl)
  const setReturnToUrl = (url: string | null): void => patchPickerState({ returnToUrl: url })

  /**
   * Switches back to the screen-recording session and restores the URL
   * the user was on before recording started.
   */
  function restoreScreenRecordingSession(): void {
    const screenSessionId = screenRecordingSessionId.value
    if (screenSessionId == null) return

    globalStore.switchToSessionId(screenSessionId)

    const savedReturnToUrl = returnToUrl.value
    if (savedReturnToUrl && savedReturnToUrl !== window.location.href) {
      window.history.pushState(null, '', savedReturnToUrl)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (hasUnsavedScreenWork.value) {
      event.preventDefault()
    }
  }

  watch(hasUnsavedScreenWork, (hasWork) => {
    if (hasWork) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  // #endregion Navigation

  // #region Recording state

  const activeScreenRecording = shallowRef<ActiveScreenRecording | null>(null)
  const streamSecondsElapsed = ref(0)
  const isMicAudioEnabled = ref(false)

  const recordingOptions = computed<{ mimeType: string; videoBitsPerSecond?: number }>(() => {
    if (device.firefox) {
      return { mimeType: supportedScreenRecordingCodec.split(';')[0] }
    } else if (device.safari) {
      return { mimeType: supportedScreenRecordingCodec, videoBitsPerSecond: 3_000_000 }
    } else {
      return { mimeType: supportedScreenRecordingCodec }
    }
  })

  const { isSupported: isWakeLockSupported, request: requestWakeLock, release: releaseWakeLock } = useWakeLock()

  const finalizedBlob = shallowRef<Blob | null>(null)

  const hasReachedRecordingLimit = computed<boolean>(() => {
    return streamSecondsElapsed.value >= screenRecordingLengthLimit.value
  })

  // #endregion Recording state

  // #region Recording helpers

  /**
   * Assembles a blob from recorded chunks and fixes WebM duration metadata.
   */
  async function buildRecordingBlob(recording: ActiveScreenRecording): Promise<Blob> {
    let blob = new Blob(recording.streamChunks, { type: recordingOptions.value.mimeType })
    if (
      recordingOptions.value.mimeType.startsWith('video/webm') ||
      recordingOptions.value.mimeType.startsWith('audio/webm')
    ) {
      blob = await ysFixWebmDuration(blob, recording.recordingDurationMs)
    }
    return blob
  }

  /** Promise tracking the in-flight blob finalization started by the onstop handler. */
  let blobFinalizationPromise: Promise<void> | null = null

  /**
   * Builds the final blob from recorded chunks, transitions to Previewing,
   * and restores the screen recording session.
   */
  async function finalizeRecording(recording: ActiveScreenRecording): Promise<void> {
    try {
      finalizedBlob.value = await buildRecordingBlob(recording)
      setScreenRecorderStatus(ScreenRecorderStatus.Previewing)
    } catch (error) {
      captureException(error)
      useGlobalSnackbarStore().setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Something went wrong finalizing your recording. Please try again.'),
      })
      setScreenRecorderStatus(ScreenRecorderStatus.Connecting)
      clearRecordingState()
    }
    restoreScreenRecordingSession()
  }

  /** Attaches dataavailable + onstop handlers to a MediaRecorder. */
  function addMediaRecorderHandlers(mediaRecorder: MediaRecorder, recording: ActiveScreenRecording): void {
    mediaRecorder.addEventListener('dataavailable', (event: BlobEvent) => {
      recording.streamChunks.push(event.data)
    })
    // Using onstop (not addEventListener) so discardRecording can clear it with onstop = null.
    mediaRecorder.onstop = () => {
      blobFinalizationPromise = finalizeRecording(recording)
    }
  }

  /** Starts a MediaRecorder, using timeslice on non-Chrome browsers. */
  function startMediaRecorder(mediaRecorder: MediaRecorder): void {
    if (device.chrome) {
      mediaRecorder.start()
    } else {
      mediaRecorder.start(SCREEN_RECORDER_TIMESLICE)
    }
  }

  /** Listens for the video track ending (user clicks "Stop sharing" in browser UI). */
  function watchVideoTrackEnded(stream: MediaStream): void {
    const videoTrack = stream.getVideoTracks()[0]
    if (videoTrack == null) return
    // Remove first to avoid duplicate listeners when the same track is reused
    // after mic addition (removeEventListener is a no-op if not registered).
    videoTrack.removeEventListener('ended', stopRecording)
    videoTrack.addEventListener('ended', stopRecording)
  }

  function enforceRecordingTimeLimit(): void {
    if (!hasReachedRecordingLimit.value) return
    stopRecording()
    useGlobalSnackbarStore().setSnackbar({
      notificationType: SnackbarNotificationType.error,
      message: p__(
        "User's screen recording has been forcibly stopped because they have reached their video recording time limit",
        'Time limit reached',
      ),
      iconName: 'exclamation',
    })
  }

  // #endregion Recording helpers

  // #region Recording lifecycle

  /**
   * Builds the ActiveScreenRecording struct and wires event handlers.
   * The closure over `streamChunks` keeps chunk collection self-contained.
   */
  function createActiveScreenRecording(
    stream: MediaStream,
    micTrack: MediaStreamTrack | null,
    videoAudioTrack: MediaStreamTrack | null,
  ): ActiveScreenRecording {
    const mediaRecorder = new MediaRecorder(stream, recordingOptions.value)
    const streamChunks: Blob[] = []

    const timerId = setInterval(() => {
      streamSecondsElapsed.value++
      enforceRecordingTimeLimit()
    }, 1000)

    const recording: ActiveScreenRecording = {
      mediaRecorder,
      stream,
      streamChunks,
      microphoneTrack: micTrack,
      videoAudioTrack,
      mediaRecorderTimerId: timerId,
      recordingDurationMs: 0,
      recordingStartTimeMs: Date.now(),
    }

    addMediaRecorderHandlers(mediaRecorder, recording)
    watchVideoTrackEnded(stream)

    return recording
  }

  /**
   * Resets state, creates the recording, registers it, and kicks off the MediaRecorder.
   * All recording state (chunks, timer, tracks) lives on the store object so it
   * survives component unmount.
   */
  function startRecording(
    stream: MediaStream,
    micTrack: MediaStreamTrack | null,
    videoAudioTrack: MediaStreamTrack | null,
    isMicEnabled: boolean,
  ): void {
    blobFinalizationPromise = null
    streamSecondsElapsed.value = 0
    isMicAudioEnabled.value = isMicEnabled

    const recording = createActiveScreenRecording(stream, micTrack, videoAudioTrack)
    activeScreenRecording.value = recording

    if (isWakeLockSupported.value) {
      void requestWakeLock('screen')
    }

    startMediaRecorder(recording.mediaRecorder)

    trackEvent('Screen recorder', 'Started')
    screenRecorderConfig.value?.handlers?.onShow?.()
    setScreenRecorderStatus(ScreenRecorderStatus.Recording)
    setReturnToUrl(window.location.href)
  }

  function stopRecording(): void {
    const rec = activeScreenRecording.value
    if (rec == null) return

    // Stop the media recorder
    if (rec.mediaRecorder.state === 'recording') {
      rec.recordingDurationMs += Date.now() - rec.recordingStartTimeMs
      rec.mediaRecorder.stop()
    } else if (rec.mediaRecorder.state === 'paused') {
      rec.mediaRecorder.stop()
    }

    // Stop timer
    if (rec.mediaRecorderTimerId != null) clearInterval(rec.mediaRecorderTimerId)
    rec.mediaRecorderTimerId = null
    rec.recordingStartTimeMs = 0

    // Stop all tracks
    rec.stream.getTracks().forEach((track) => track.stop())
    if (rec.videoAudioTrack != null) rec.videoAudioTrack.stop()
    if (rec.microphoneTrack != null) rec.microphoneTrack.stop()

    if (isWakeLockSupported.value) {
      void releaseWakeLock()
    }

    setScreenRecorderStatus(ScreenRecorderStatus.Paused)
  }

  function discardRecording(): void {
    const rec = activeScreenRecording.value
    if (rec == null) return

    // Prevent the stop handler from triggering finalization
    rec.mediaRecorder.onstop = null

    stopRecording()
    clearRecordingState()
    setScreenRecorderStatus(ScreenRecorderStatus.Connecting)
  }

  function restartRecording(): void {
    trackEvent('Screen recorder', 'Restarted')
    try {
      discardRecording()
    } catch (error) {
      captureException(error)
    } finally {
      setScreenRecorderStatus(ScreenRecorderStatus.Connecting)
    }
  }

  function showDiscardDialog(): void {
    useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...ALERT_ICON,
      title: __('You have unsaved changes'),
      body: __('Are you sure you want to discard your recording?'),
      confirmButtonText: __('Discard'),
      cancelButtonText: __('Stay here'),
      afterConfirmActions: [() => restartRecording()],
      afterCancelActions: [],
      shouldFadeIn: false,
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  function showTrimDiscardDialog(): void {
    useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Recording new video will erase video that has already been trimmed'),
      body: __('Are you sure you want to continue with the recording?'),
      confirmButtonText: __('Record more'),
      cancelButtonText: __('Keep editing'),
      afterConfirmActions: [() => restartRecording()],
      afterCancelActions: [],
      shouldFadeIn: false,
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  function confirmRestartRecording({ hasUnsavedTrimChanges }: { hasUnsavedTrimChanges: boolean }): void {
    if (hasUnsavedTrimChanges) {
      showTrimDiscardDialog()
    } else if (screenRecorderStatus.value === ScreenRecorderStatus.Previewing) {
      showDiscardDialog()
    } else {
      restartRecording()
    }
  }

  async function playbackRecording(): Promise<void> {
    trackEvent('Screen recorder', 'Finished recording video', null, streamSecondsElapsed.value)

    const rec = activeScreenRecording.value
    if (rec == null) return

    // If the MediaRecorder is still active, stop it — the onstop handler
    // will finalize the blob and transition to Previewing.
    if (rec.mediaRecorder.state !== 'inactive') {
      stopRecording()
      return
    }

    // The MediaRecorder is already inactive — the onstop handler is building the blob.
    // Await it so clicking "Next" isn't a no-op for long recordings.
    if (blobFinalizationPromise != null) {
      await blobFinalizationPromise
      return
    }

    // Safety net: onstop never fired (e.g. handler was cleared). Finalize directly.
    await finalizeRecording(rec)
  }

  function clearRecordingState(): void {
    activeScreenRecording.value = null
    finalizedBlob.value = null
    blobFinalizationPromise = null
    streamSecondsElapsed.value = 0
    isMicAudioEnabled.value = false
  }

  /**
   * Acquires a mic mid-recording, stops the current MediaRecorder without finalizing,
   * builds a combined stream, and restarts with the new mic mixed in.
   */
  async function addMicToActiveRecording(rec: ActiveScreenRecording): Promise<void> {
    const audioDevicesStore = useAudioDevicesStore()
    let newMicTrack: MediaStreamTrack | undefined
    try {
      const micStream = await navigator.mediaDevices.getUserMedia(audioDevicesStore.audioConstraints)
      newMicTrack = micStream.getAudioTracks()[0]
      if (newMicTrack == null) return

      // The recording may have been stopped or discarded while we were awaiting getUserMedia
      if (activeScreenRecording.value !== rec || screenRecorderStatus.value !== ScreenRecorderStatus.Recording) {
        newMicTrack.stop()
        return
      }

      // Prevent the current onstop handler from finalizing a blob
      rec.mediaRecorder.onstop = null

      // Stop the current MediaRecorder (fires a final dataavailable, chunks are preserved)
      if (rec.mediaRecorder.state !== 'inactive') {
        rec.recordingDurationMs += Date.now() - rec.recordingStartTimeMs
        rec.mediaRecorder.stop()
      }

      // Reconstruct a display stream from the current video + system audio tracks
      const videoTracks = rec.stream.getVideoTracks()
      const systemAudioTrack = rec.stream.getAudioTracks()[0]
      const displayStream =
        systemAudioTrack != null ? new MediaStream([...videoTracks, systemAudioTrack]) : new MediaStream(videoTracks)

      // Mix system audio and the new mic audio
      const { combinedStream, micTrack, videoAudioTrack } = getMixedSystemAndMicAudio(
        displayStream,
        new MediaStream([newMicTrack]),
      )

      // Create a new MediaRecorder with the combined stream
      const newMediaRecorder = new MediaRecorder(combinedStream, recordingOptions.value)
      addMediaRecorderHandlers(newMediaRecorder, rec)
      startMediaRecorder(newMediaRecorder)

      // Update the recording object in place (preserves streamChunks reference)
      rec.mediaRecorder = newMediaRecorder
      rec.stream = combinedStream
      rec.microphoneTrack = micTrack
      rec.videoAudioTrack = videoAudioTrack
      rec.recordingStartTimeMs = Date.now()

      watchVideoTrackEnded(combinedStream)

      isMicAudioEnabled.value = true
    } catch (error) {
      newMicTrack?.stop()
      captureException(error)
    }
  }

  /**
   * Toggles the microphone during recording.
   * If a mic track already exists, simply toggles its enabled state.
   * If no mic track exists (user started without a mic), acquires a new mic stream,
   * mixes it with the existing audio, and restarts the MediaRecorder seamlessly
   * while preserving accumulated chunks.
   */
  async function toggleMic(): Promise<void> {
    const rec = activeScreenRecording.value
    if (rec == null) return

    if (rec.microphoneTrack == null) {
      await addMicToActiveRecording(rec)
      return
    }

    const toggled = !rec.microphoneTrack.enabled
    rec.microphoneTrack.enabled = toggled
    isMicAudioEnabled.value = toggled
  }

  // #endregion Recording lifecycle

  // #region Output

  function createVideoFile(): File | null {
    if (finalizedBlob.value == null) return null
    if (!supportedScreenRecordingExtension) {
      captureMessage('Unable to get supported video extension for recorder', {
        context: { supportedScreenRecordingCodec, supportedAudioCodec },
      })
      return null
    }
    return new File([finalizedBlob.value], `video.${supportedScreenRecordingExtension}`, {
      type: finalizedBlob.value.type,
    })
  }

  // #endregion Output

  // #region Recording bar

  const recordingBarController = computed<RecordingBarController | null>(() => {
    if (activeScreenRecording.value == null) return null
    const elapsed = streamSecondsElapsed.value
    const limit = screenRecordingLengthLimit.value
    return {
      elapsedRecordingTimeDisplay: secondsToMmSs(elapsed),
      timeLimitDisplay: secondsToMmSs(limit),
      isReachingRecordingLimit: elapsed >= limit - 5,
      isMicOn: isMicAudioEnabled.value,
      stopRecording: () => stopRecording(),
      toggleMic: () => toggleMic(),
    }
  })

  // #endregion Recording bar

  return {
    // Config
    screenRecorderConfig,
    screenRecordingLengthLimit,
    currentScreenRecordingLengthLimitSetting,

    // Status
    screenRecorderStatus,
    setScreenRecorderStatus,
    isScreenRecordingInProgress,
    hasUnsavedScreenWork,
    hasStartedRecordingScreen,

    // Close guard
    showDiscardDialog,
    confirmRestartRecording,

    // Recording lifecycle
    activeScreenRecording,
    streamSecondsElapsed,
    isMicAudioEnabled,
    recordingOptions,
    startRecording,
    discardRecording,
    restartRecording,
    playbackRecording,
    clearRecordingState,
    hasReachedRecordingLimit,

    // Recording bar
    recordingBarController,

    // Output
    finalizedBlob,
    createVideoFile,
  }
})

export { useContentPickerAvScreenRecorderStore }
export type { ActiveScreenRecording, RecordingBarController }
